import type { MbcBriefAssetTarget } from "@nlc/shared-types";
import type { Variants } from "framer-motion";

import { motion } from "framer-motion";
import { useCallback, useMemo, useState } from "react";

import { toast } from "@/components/toast/toast-queue";
import { useGetBrandById } from "@/features/brands/shared/hooks/useGetBrandById";

import type { AssetGroupChange } from "../mbc-chat/components/upload-assets/types";
import type { Deliverables } from "./components/EditSizeModal";

import { AssetUploadBlock } from "../mbc-chat/components/upload-assets/AssetUploadBlock";
import { readGroup } from "../mbc-chat/components/upload-assets/types";
import { requestIdFromSessionId } from "../mbc-chat/lib/session-ids";
import { AiImagesCard } from "./components/AiImagesCard";
import { BriefCard } from "./components/BriefCard";
import { BriefQualityCard } from "./components/BriefQualityCard";
import { CollapsibleCard } from "./components/CollapsibleCard";
import { CreativeDirectionCard } from "./components/CreativeDirectionCard";
import { DeliverablesCard } from "./components/DeliverablesCard";
import { MBCTitleBar } from "./components/MBCTitleBar";
import { PeopleCard } from "./components/PeopleCard";
import { ReviewFooter } from "./components/ReviewFooter";
import { TextForDesign } from "./components/TextForDesign";
import { mapBriefToReview } from "./map-brief";
import { useDesignTypeName } from "./useDesignTypeName";
import { useReviewBrief } from "./useReviewBrief";

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { delayChildren: 0.05, staggerChildren: 0.08 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, transition: { duration: 0.35, ease: "easeOut" }, y: 0 },
};

type MBCReviewProps = {
  brandId?: number;
  designTypeId?: number;
  onBack?: () => void;
  onBackToChat?: () => void;
  /** Fired once the request is submitted, so the caller can navigate away. */
  onSubmitted?: (requestId: number) => void;
  resourceId?: string;
  threadId?: string;
};

export function MBCReview({
  brandId,
  designTypeId,
  onBack,
  onBackToChat,
  onSubmitted,
  resourceId,
  threadId,
}: MBCReviewProps) {
  const {
    brief,
    isSaving,
    isSubmitting,
    patchBrief,
    quality,
    refetchBrief,
    submitBrief,
  } = useReviewBrief({
    resourceId,
    threadId,
  });
  const { data: brand } = useGetBrandById(brandId ?? 0);
  const designTypeName = useDesignTypeName(designTypeId);
  const [uploadsInFlight, setUploadsInFlight] = useState(0);
  const trackUpload = useCallback(
    (delta: -1 | 1) => setUploadsInFlight((count) => count + delta),
    [],
  );

  const data = useMemo(
    () => mapBriefToReview(brief, designTypeName),
    [brief, designTypeName],
  );

  const assets = useMemo(() => readGroup(brief?.assets), [brief?.assets]);
  const inspirations = useMemo(
    () => readGroup(brief?.inspirations),
    [brief?.inspirations],
  );

  // Edits persist to the working memory only when we have a brief to edit.
  const canEdit = Boolean(threadId);

  const requestId = threadId ? requestIdFromSessionId(threadId) : null;

  const handleFilesChanged = useCallback(() => {
    void refetchBrief();
    void patchBrief({}).catch(refetchBrief);
  }, [patchBrief, refetchBrief]);

  const handleAssetChange = useCallback(
    (target: MbcBriefAssetTarget, change: AssetGroupChange) =>
      void patchBrief({ assetChanges: [{ target, ...change }] }),
    [patchBrief],
  );

  const handleSubmit = useCallback(() => {
    submitBrief()
      .then((result) => onSubmitted?.(result.requestId))
      .catch((error: Error) => {
        toast.add({
          description: error.message,
          title: "Couldn't submit your brief",
          type: "error",
        });
      });
  }, [submitBrief, onSubmitted]);

  const handleTitleSave = useCallback(
    (value: string) => void patchBrief({ title: value }),
    [patchBrief],
  );

  const handleVisualDirectionSave = useCallback(
    (value: string) => void patchBrief({ visualDirection: value }),
    [patchBrief],
  );

  const handleTextForDesignSave = useCallback(
    (value: string) => void patchBrief({ textForDesign: value }),
    [patchBrief],
  );

  const handleDeliverablesSave = useCallback(
    ({ fileSizes, fileTypes }: Deliverables) =>
      void patchBrief({
        fileSizes: fileSizes.map((item) => ({
          dimensions: item.size,
          label: item.label,
        })),
        fileTypes,
      }),
    [patchBrief],
  );

  return (
    <div className="relative flex min-h-full flex-col bg-bg-secondary">
      <MBCTitleBar
        brand={
          brand ? { logoUrl: brand.profilePicture, name: brand.name } : null
        }
        designTypeName={designTypeName}
        onBack={onBack}
        onTitleSave={canEdit ? handleTitleSave : undefined}
        title={data.brief.title || "Untitled brief"}
      />

      {/* Body */}
      <div className="relative flex-1">
        <motion.div
          animate="show"
          className="mx-auto flex max-w-[1240px] flex-col gap-7 p-4xl lg:flex-row lg:items-start"
          initial="hidden"
          variants={containerVariants}
        >
          {/* Left column */}
          <div className="flex min-w-0 flex-1 flex-col gap-5">
            <motion.div variants={itemVariants}>
              <BriefCard
                brief={data.brief}
                onVisualDirectionSave={
                  canEdit ? handleVisualDirectionSave : undefined
                }
              />
            </motion.div>

            <motion.div variants={itemVariants}>
              <TextForDesign
                onSave={canEdit ? handleTextForDesignSave : undefined}
                value={data.brief.textForDesign}
              />
            </motion.div>

            <motion.div variants={itemVariants}>
              <CollapsibleCard title="Assets">
                <AssetUploadBlock
                  onChange={(change) => handleAssetChange("assets", change)}
                  onFilesChanged={handleFilesChanged}
                  onUploadingChange={trackUpload}
                  requestId={requestId}
                  target="assets"
                  title="Assets"
                  value={assets}
                />
              </CollapsibleCard>
            </motion.div>
            <motion.div variants={itemVariants}>
              <CollapsibleCard title="Inspirations">
                <AssetUploadBlock
                  onChange={(change) =>
                    handleAssetChange("inspirations", change)
                  }
                  onFilesChanged={handleFilesChanged}
                  onUploadingChange={trackUpload}
                  requestId={requestId}
                  target="inspirations"
                  title="Inspirations"
                  value={inspirations}
                />
              </CollapsibleCard>
            </motion.div>
          </div>

          {/* Right column */}
          <div className="flex w-full flex-col gap-5 lg:w-[358px] lg:shrink-0">
            <motion.div variants={itemVariants}>
              <BriefQualityCard
                isCalculating={isSaving || uploadsInFlight > 0}
                quality={quality}
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <DeliverablesCard
                fileSizes={data.brief.fileSizes}
                fileTypes={data.brief.fileTypes}
                onChange={canEdit ? handleDeliverablesSave : undefined}
              />
            </motion.div>
            <motion.div variants={itemVariants}>
              <AiImagesCard requestId={requestId} />
            </motion.div>
            <motion.div variants={itemVariants}>
              <CreativeDirectionCard requestId={requestId} />
            </motion.div>
            <motion.div variants={itemVariants}>
              <PeopleCard requestId={requestId} />
            </motion.div>
          </div>
        </motion.div>
      </div>

      <ReviewFooter
        canSubmit={quality?.canSubmit === true}
        isSubmitting={isSubmitting}
        isUploading={uploadsInFlight > 0}
        onBackToChat={onBackToChat}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
